import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  CButton,
  CCard,
  CFormSwitch,
  CLoadingButton,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from '@coreui/react-pro'
import { cilCheckCircle, cilLink, cilPlus, cilSync, cilWarning, cilX } from '@coreui/icons'
import ToastComponent from 'src/components/common/TaostComponent'
import {
  ImportErrorAlert,
  InfoBox,
  MiniStepper,
  StatTile,
} from 'src/components/common/ImportWizard'
import ClubStatsService from 'src/service/ClubStatsService'
import {
  ACTIONS,
  ActionSelect,
  BulkBar,
  CHECK_CELL_STYLE,
  BulkSelect,
  FilterPills,
  LoadingBlock,
  MatchBadge,
  RecordSelect,
  RowCheckbox,
  SelectAllCheckbox,
  duplicateTargets,
  errorsOf,
  matchRecord,
  reportBulk,
  rowStyle,
  tableHeadStyle,
  useRowSelection,
} from './clubstatsShared'

const STEPS = ['Validate', 'Review & Import']

/**
 * The ClubStats import for records that are either one of ours already or new: grades, teams and
 * players. Each row is a ClubStats record; the admin says whether it is one of theirs (link, or
 * link and take ClubStats' details) or a new one (create), or leaves it out. What differs between
 * grades, teams and players - the extra columns, what a new record needs, the payload - comes
 * in through `config`.
 *
 * With `activation` ({ inactive: Set of ClubStats ids, onChange(ids, active) }) each row also gets
 * a Status switch - used by grades, whose deactivated ones the other tabs leave out. Switching
 * saves straight away. Deactivated rows are not imported either: they are shown like skipped
 * ones and never hold the import up.
 */
const LinkImporter = ({ type, teamIds, config, activation, onImported }) => {
  const [data, setData] = useState(null)
  const [rows, setRows] = useState([])
  const [loadErrors, setLoadErrors] = useState([])
  const [step, setStep] = useState(1)
  const [filter, setFilter] = useState('all')
  const [importing, setImporting] = useState(false)
  const [importErrors, setImportErrors] = useState([])
  const [result, setResult] = useState(null)
  const selection = useRowSelection()
  const clearSelection = selection.clear
  // Keys of rows whose Status is being saved.
  const [switching, setSwitching] = useState(() => new Set())
  // Players only (`config.analyticValueToggle`): off takes ClubStats' flat starting value (9);
  // on asks ClubStats for each player's value from last season's performance.
  const [analyticValue, setAnalyticValue] = useState(false)

  const records = useMemo(() => (data ? data[config.recordsKey] || [] : []), [data, config])

  const load = useCallback(() => {
    setData(null)
    setLoadErrors([])
    setResult(null)
    setImportErrors([])
    setStep(1)
    clearSelection()
    ClubStatsService.preview(type, {
      team_ids: teamIds,
      ...(config.analyticValueToggle && analyticValue ? { use_analytic_value: 1 } : {}),
    })
      .then((res) => {
        if (!res.success) {
          setLoadErrors(errorsOf(res))
          return
        }
        const ours = res.data[config.recordsKey] || []
        setData(res.data)
        const items = res.data.items || []
        // By ClubStats id or name first. Records found that way are taken, so a suggestion made
        // afterwards (a team by its grade, say) never points two rows at the same record.
        const found = items.map((item) =>
          matchRecord(
            ours,
            config.itemClubstatsId(item),
            config.itemName(item),
            config.getRecordName,
          ),
        )
        const claimed = new Set(found.filter((f) => f.record).map((f) => String(f.record.id)))
        if (config.suggest) {
          found.forEach((f, i) => {
            if (f.how !== 'none') return
            const suggestion = config.suggest(items[i], res.data, ours, claimed)
            if (suggestion) {
              found[i] = { record: suggestion, how: 'grade' }
              claimed.add(String(suggestion.id))
            }
          })
        }
        setRows(
          items.map((item, i) => {
            const match = found[i]
            const action =
              (config.initialAction && config.initialAction(match)) ||
              (match.how === 'linked' ? 'skip' : match.how === 'none' ? 'create' : 'link')
            return {
              key: config.itemClubstatsId(item) || i,
              item,
              how: match.how,
              linked_id: match.how === 'linked' ? match.record.id : null,
              action,
              target_id: match.record ? String(match.record.id) : '',
              ...config.initialExtra(item, res.data, match.record),
            }
          }),
        )
      })
      .catch(() => setLoadErrors(['ClubStats data could not be loaded. Please try again.']))
  }, [type, teamIds, config, clearSelection, analyticValue])

  useEffect(() => {
    load()
  }, [load])

  const updateRow = (key, changes) =>
    setRows((current) =>
      current.map((row) => {
        if (row.key !== key) return row
        const next = { ...row, ...changes }
        if ('target_id' in changes) next.how = changes.target_id ? 'manual' : 'none'
        return next
      }),
    )

  // Applies a change to every ticked row. `changesFor` returns the change for one row, or null
  // where it does not apply (a grade on a row that is only being linked, say).
  const applyBulk = (changesFor, what) => {
    let applied = 0
    let refused = 0
    const next = rows.map((row) => {
      if (!selection.selected.has(row.key)) return row
      const changes = changesFor(row)
      if (!changes) {
        refused++
        return row
      }
      applied++
      return { ...row, ...changes }
    })
    setRows(next)
    reportBulk(applied, refused, what)
  }

  const inactive = activation?.inactive
  const checked = useMemo(() => {
    const dupes = duplicateTargets(rows)
    return rows.map((row) => {
      const off = Boolean(inactive && inactive.has(row.key))
      let problem = null
      if (row.action !== 'skip' && !off) {
        if ((row.action === 'link' || row.action === 'update') && !row.target_id) {
          problem = `Choose which of your ${config.nounPlural} this is.`
        } else if (row.action !== 'create' && dupes.has(String(row.target_id))) {
          problem = `Another row is linked to the same ${config.noun}.`
        } else {
          problem = config.rowProblem ? config.rowProblem(row) : null
        }
      }
      return {
        ...row,
        off,
        problem,
        tone: row.action === 'skip' || off ? 'skipped' : problem ? 'attention' : 'ready',
      }
    })
  }, [rows, config, inactive])

  const counts = useMemo(() => {
    const c = {
      all: checked.length,
      attention: 0,
      create: 0,
      link: 0,
      update: 0,
      skip: 0,
      linked: 0,
      ready: 0,
      off: 0,
    }
    checked.forEach((row) => {
      if (row.tone === 'attention') c.attention++
      if (row.tone === 'ready') c.ready++
      if (row.off) c.off++
      c[row.action]++
      if (row.how === 'linked') c.linked++
    })
    return c
  }, [checked])

  // What the import takes: the ticked rows when any are ticked, else every row not skipped -
  // never a deactivated one. Only those rows' problems hold it up.
  const ticked = selection.selected.size > 0
  const toImport = useMemo(
    () =>
      checked.filter(
        (row) => row.action !== 'skip' && !row.off && (!ticked || selection.selected.has(row.key)),
      ),
    [checked, ticked, selection.selected],
  )
  const importCounts = useMemo(() => {
    const c = { attention: 0, create: 0, link: 0, update: 0 }
    toImport.forEach((row) => {
      if (row.tone === 'attention') c.attention++
      c[row.action]++
    })
    return c
  }, [toImport])

  const visible = checked.filter((row) => {
    if (filter === 'attention') return row.tone === 'attention'
    if (filter === 'create') return row.action === 'create'
    if (filter === 'existing') return row.action === 'link' || row.action === 'update'
    if (filter === 'skip') return row.action === 'skip'
    if (filter === 'off') return row.off
    return true
  })

  const switchActive = async (keys, active) => {
    setSwitching((current) => new Set([...current, ...keys]))
    try {
      await activation.onChange(keys, active)
    } finally {
      setSwitching((current) => new Set([...current].filter((key) => !keys.includes(key))))
    }
  }

  const runImport = async () => {
    setImporting(true)
    setImportErrors([])
    try {
      const payload = toImport.map((row) => config.toPayload(row))
      const res = await ClubStatsService.runImport(type, { rows: payload })
      if (res.success) {
        ToastComponent(res.message || 'Imported.', 'success')
        setResult(res.message)
        if (onImported) onImported()
      } else {
        setImportErrors(errorsOf(res))
      }
    } catch (e) {
      setImportErrors(['The import could not be run. Please try again.'])
    } finally {
      setImporting(false)
    }
  }

  if (loadErrors.length) {
    return (
      <>
        <ImportErrorAlert errors={loadErrors} title="ClubStats data could not be loaded:" />
        <CButton color="primary" variant="outline" onClick={load}>
          Try again
        </CButton>
      </>
    )
  }
  if (!data) return <LoadingBlock text={config.loadingText} />

  if (result) {
    return (
      <>
        <InfoBox color="success">{result}</InfoBox>
        <CButton color="primary" variant="outline" onClick={load}>
          Review {config.nounPlural} again
        </CButton>
      </>
    )
  }

  if (!rows.length) {
    return <InfoBox color="light">ClubStats has no {config.nounPlural} here.</InfoBox>
  }

  const recordLabel = (id) => {
    const record = records.find((r) => String(r.id) === String(id))
    return record ? config.getRecordLabel(record) : '—'
  }

  const renderValidate = () => (
    <>
      {config.intro && <InfoBox color="light">{config.intro}</InfoBox>}
      {config.analyticValueToggle && (
        <div className="border rounded-4 p-3 mb-3" style={tableHeadStyle}>
          <CFormSwitch
            id="clubstats-analytic-value"
            label="Use ClubStats’ performance-based values"
            checked={analyticValue}
            onChange={(e) => setAnalyticValue(e.target.checked)}
          />
          <div className="small text-medium-emphasis mt-1">
            {analyticValue
              ? 'Each player’s value comes from last season’s performance (6 to 15). Players with no usable data from last season start on 9.'
              : 'Every player starts on ClubStats’ flat value of 9. Turn this on to value each player by last season’s performance.'}{' '}
            New players take your game’s price nearest that value.
          </div>
        </div>
      )}
      <div className="d-flex gap-3 flex-wrap mb-3">
        <StatTile label="Ready" value={counts.ready} tone="success" icon={cilCheckCircle} />
        <StatTile
          label="Needs attention"
          value={counts.attention}
          tone="warning"
          icon={cilWarning}
        />
        <StatTile label="Already linked" value={counts.linked} tone="info" icon={cilLink} />
      </div>
      <FilterPills
        value={filter}
        onChange={setFilter}
        filters={[
          { key: 'all', label: 'All', count: counts.all },
          { key: 'attention', label: 'Needs attention', count: counts.attention },
          { key: 'existing', label: 'Link to existing', count: counts.link + counts.update },
          { key: 'create', label: 'Create new', count: counts.create },
          { key: 'skip', label: 'Skipped', count: counts.skip },
          ...(activation ? [{ key: 'off', label: 'Deactivated', count: counts.off }] : []),
        ]}
      />
      {activation && (
        <div className="d-flex align-items-center flex-wrap gap-2 mb-2">
          <span className="small text-medium-emphasis me-auto">
            {counts.all - counts.off} of {counts.all} {config.nounPlural} activated. A deactivated{' '}
            {config.noun} is not imported, and its teams, players, rounds, fixtures and squads are
            left out of the other tabs.
          </span>
          <CButton
            size="sm"
            color="secondary"
            variant="outline"
            disabled={!counts.off || switching.size > 0}
            onClick={() =>
              switchActive(
                rows.map((row) => row.key),
                true,
              )
            }
          >
            Activate all
          </CButton>
          <CButton
            size="sm"
            color="secondary"
            variant="outline"
            disabled={counts.off === counts.all || switching.size > 0}
            onClick={() =>
              switchActive(
                rows.map((row) => row.key),
                false,
              )
            }
          >
            Deactivate all
          </CButton>
        </div>
      )}
      <BulkBar count={selection.selected.size} onClear={selection.clear}>
        {activation && (
          <>
            <CButton
              size="sm"
              color="success"
              variant="outline"
              disabled={switching.size > 0}
              onClick={() => switchActive([...selection.selected], true)}
            >
              Activate
            </CButton>
            <CButton
              size="sm"
              color="danger"
              variant="outline"
              disabled={switching.size > 0}
              onClick={() => switchActive([...selection.selected], false)}
            >
              Deactivate
            </CButton>
          </>
        )}
        <BulkSelect
          placeholder="Set action…"
          options={Object.entries(ACTIONS).map(([value, a]) => ({ value, label: a.label }))}
          onPick={(action) => applyBulk(() => ({ action }), `"${ACTIONS[action].label}"`)}
        />
        {(config.bulkFields || []).map((field) => (
          <BulkSelect
            key={field.key}
            placeholder={field.placeholder}
            options={(data[field.recordsKey] || []).map((r) => ({
              value: String(r.id),
              label: field.getLabel(r),
            }))}
            onPick={(value) =>
              applyBulk(
                (row) => (field.appliesTo(row) ? { [field.key]: value } : null),
                field.label,
              )
            }
          />
        ))}
      </BulkBar>
      <CCard className="border-secondary-subtle rounded-4 overflow-hidden mb-3">
        <div className="table-responsive">
          <CTable align="middle" hover className="mb-0">
            <CTableHead className="align-middle" style={tableHeadStyle}>
              <CTableRow>
                <CTableHeaderCell className="ps-3" style={CHECK_CELL_STYLE}>
                  <SelectAllCheckbox keys={visible.map((row) => row.key)} selection={selection} />
                </CTableHeaderCell>
                <CTableHeaderCell>ClubStats {config.noun}</CTableHeaderCell>
                {config.itemColumns.map((col) => (
                  <CTableHeaderCell key={col.header}>{col.header}</CTableHeaderCell>
                ))}
                <CTableHeaderCell>Action</CTableHeaderCell>
                <CTableHeaderCell>Your {config.noun}</CTableHeaderCell>
                {config.extraColumns.map((col) => (
                  <CTableHeaderCell key={col.header}>{col.header}</CTableHeaderCell>
                ))}
                {activation && <CTableHeaderCell>Status</CTableHeaderCell>}
              </CTableRow>
            </CTableHead>
            <CTableBody>
              {visible.map((row) => (
                <CTableRow key={row.key} style={rowStyle(row.tone)}>
                  <CTableDataCell className="ps-3">
                    <RowCheckbox
                      checked={selection.selected.has(row.key)}
                      onChange={() => selection.toggle(row.key)}
                      title={`Select ${config.itemName(row.item)}`}
                    />
                  </CTableDataCell>
                  <CTableDataCell>
                    <div className="fw-semibold">{config.itemName(row.item)}</div>
                    <MatchBadge how={row.how} />
                    {row.problem && <div className="small text-warning mt-1">{row.problem}</div>}
                  </CTableDataCell>
                  {config.itemColumns.map((col) => (
                    <CTableDataCell key={col.header}>{col.cell(row, data)}</CTableDataCell>
                  ))}
                  <CTableDataCell>
                    <ActionSelect
                      value={row.action}
                      onChange={(action) => updateRow(row.key, { action })}
                    />
                  </CTableDataCell>
                  <CTableDataCell>
                    {row.action === 'link' || row.action === 'update' ? (
                      <RecordSelect
                        value={row.target_id}
                        onChange={(target_id) => updateRow(row.key, { target_id })}
                        records={records}
                        getLabel={config.getRecordLabel}
                        placeholder={`Choose ${config.noun}…`}
                        invalid={!row.target_id}
                      />
                    ) : row.action === 'create' ? (
                      <span className="small text-medium-emphasis">
                        New: {config.itemName(row.item)}
                      </span>
                    ) : row.linked_id ? (
                      <span className="small">{recordLabel(row.linked_id)}</span>
                    ) : (
                      '—'
                    )}
                  </CTableDataCell>
                  {config.extraColumns.map((col) => (
                    <CTableDataCell key={col.header}>
                      {col.cell(row, (changes) => updateRow(row.key, changes), data)}
                    </CTableDataCell>
                  ))}
                  {activation && (
                    <CTableDataCell>
                      <CFormSwitch
                        id={`active-${row.key}`}
                        label={row.off ? 'Deactivated' : 'Activated'}
                        checked={!row.off}
                        disabled={switching.has(row.key)}
                        onChange={(e) => switchActive([row.key], e.target.checked)}
                        style={{ whiteSpace: 'nowrap' }}
                      />
                    </CTableDataCell>
                  )}
                </CTableRow>
              ))}
            </CTableBody>
          </CTable>
        </div>
      </CCard>
      <div className="d-flex justify-content-end align-items-center gap-2 flex-wrap">
        {importCounts.attention > 0 ? (
          <span className="small text-warning me-auto">
            {importCounts.attention} {ticked ? 'ticked ' : ''}
            {importCounts.attention === 1
              ? `${config.noun} needs`
              : `${config.nounPlural} need`}{' '}
            attention. Fix {importCounts.attention === 1 ? 'it' : 'them'}, set{' '}
            {importCounts.attention === 1 ? 'it' : 'them'} to Skip
            {ticked ? ', or untick' : `, or tick only the ${config.nounPlural} you want to import`}.
          </span>
        ) : (
          ticked &&
          toImport.length > 0 && (
            <span className="small text-medium-emphasis me-auto">
              Only the {toImport.length} ticked{' '}
              {toImport.length === 1 ? config.noun : config.nounPlural} will be imported.
            </span>
          )
        )}
        <CButton color="secondary" variant="outline" onClick={load}>
          Reload from ClubStats
        </CButton>
        <CButton
          color="primary"
          disabled={importCounts.attention > 0 || !toImport.length}
          onClick={() => setStep(2)}
        >
          Review {toImport.length} {toImport.length === 1 ? config.noun : config.nounPlural}
        </CButton>
      </div>
    </>
  )

  const renderReview = () => {
    return (
      <>
        <div className="d-flex gap-3 flex-wrap mb-3">
          <StatTile label="Create new" value={importCounts.create} tone="success" icon={cilPlus} />
          <StatTile label="Link to existing" value={importCounts.link} tone="info" icon={cilLink} />
          <StatTile
            label="Update from ClubStats"
            value={importCounts.update}
            tone="warning"
            icon={cilSync}
          />
          <StatTile
            label={ticked ? 'Not imported' : 'Skipped'}
            value={counts.all - toImport.length}
            tone="secondary"
            icon={cilX}
          />
        </div>
        {config.reviewNote && <InfoBox color="light">{config.reviewNote}</InfoBox>}
        <CCard className="border-secondary-subtle rounded-4 overflow-hidden mb-3">
          <div className="table-responsive">
            <CTable align="middle" className="mb-0">
              <CTableHead className="align-middle" style={tableHeadStyle}>
                <CTableRow>
                  <CTableHeaderCell className="ps-3">ClubStats {config.noun}</CTableHeaderCell>
                  <CTableHeaderCell>What happens</CTableHeaderCell>
                  <CTableHeaderCell>Your {config.noun}</CTableHeaderCell>
                </CTableRow>
              </CTableHead>
              <CTableBody>
                {toImport.map((row) => (
                  <CTableRow key={row.key}>
                    <CTableDataCell className="ps-3">{config.itemName(row.item)}</CTableDataCell>
                    <CTableDataCell>{ACTIONS[row.action].label}</CTableDataCell>
                    <CTableDataCell>
                      {row.action === 'create'
                        ? config.describeNew
                          ? config.describeNew(row, data)
                          : config.itemName(row.item)
                        : recordLabel(row.target_id)}
                    </CTableDataCell>
                  </CTableRow>
                ))}
              </CTableBody>
            </CTable>
          </div>
        </CCard>
        <ImportErrorAlert errors={importErrors} />
        <div className="d-flex justify-content-end gap-2">
          <CButton color="secondary" variant="outline" onClick={() => setStep(1)}>
            Back
          </CButton>
          <CLoadingButton color="primary" loading={importing} onClick={runImport}>
            Import {toImport.length} {toImport.length === 1 ? config.noun : config.nounPlural}
          </CLoadingButton>
        </div>
      </>
    )
  }

  return (
    <>
      <MiniStepper steps={STEPS} current={step} maxReached={step} onStepClick={setStep} />
      {step === 1 ? renderValidate() : renderReview()}
    </>
  )
}

export default LinkImporter
